import React from 'react';
import { Divider, Space } from 'antd';
import moment from 'moment';
import { Organisation } from 'store/organisation/models';
import { AdvancedUserRightsCodes, MenuSize } from 'store/app/enums';
import { User } from 'store/auth/models';
import { StyledCard } from 'components/Custom/card';
import { CardInfo } from './card-info';
import { CardTitleSpan } from 'components/Custom/spans';
import {
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatDisplayDate } from 'i18n/calendarLabels';
import { ProviderLogoNav } from 'components/Custom/ProviderLogoNav';
import { AccountType, Provider } from 'store/api/enums';
import ImgWithXeroSignIn from 'assets/images/xero_icon.png';
import ImgWithQBOSignIn from 'assets/images/quickbooks_icon.svg';
import ImgWithExcelSignIn from 'assets/images/excel_icon.svg';
import ImgWithZohoSignIn from 'assets/images/zoho_icon.png';
import { AiScanningLogo } from 'components/Custom/AiScanningLogo';
import { TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH } from './tax-rates-promotion-banner';

interface EntityDetailsCardProps {
  organisation?: Organisation;
  currentUser?: User;
  menuSize: MenuSize;
  windowWidth: number;
  windowHeight: number;
  onClose: () => void;
  onEditFriendlyName: () => void;
  getCountry: (iso: string) => string;
  hasAttachments: boolean;
  downloadAttachment: (organisationId: string, attachmentId: string) => void;
  ENTITY_DETAILS_BREAKPOINT: number;
  ENTITY_DETAILS_HEIGHT_BREAKPOINT: number;
  /** Fixed right column â†’ same width as Capture / Reconciliation side cards; no top offset. */
  inlineBesideMain?: boolean;
}

const renderProviderLogo = (provider?: string) => {
  const providerKey = provider?.toLowerCase();
  const alt = provider || '';

  switch (providerKey) {
    case 'group':
      return (
        <UsergroupAddOutlined
          style={{
            fontSize: 24,
            color: 'var(--color-text-base, #2e2e2e)',
            paddingTop: '4px',
          }}
        />
      );
    case Provider.QUICKBOOKS:
      return (
        <img
          src={ImgWithQBOSignIn}
          alt={alt}
          style={{ width: '36px', height: '36px' }}
        />
      );
    case Provider.EXCEL:
      return (
        <img
          src={ImgWithExcelSignIn}
          alt={alt}
          style={{ width: '36px', height: '36px' }}
        />
      );
    case Provider.ZOHO:
      return (
        <img
          src={ImgWithZohoSignIn}
          alt={alt}
          style={{ width: '36px', height: '36px' }}
        />
      );
    case Provider.RECEIPTS:
      return <AiScanningLogo size={36} />;
    default:
      return (
        <img
          src={ImgWithXeroSignIn}
          alt={alt}
          style={{ width: '36px', height: '36px' }}
        />
      );
  }
};

export const EntityDetailsCard: React.FC<EntityDetailsCardProps> = ({
  organisation,
  currentUser,
  windowWidth,
  windowHeight,
  onClose,
  onEditFriendlyName,
  getCountry,
  hasAttachments,
  downloadAttachment,
  ENTITY_DETAILS_BREAKPOINT,
  ENTITY_DETAILS_HEIGHT_BREAKPOINT,
  inlineBesideMain = false,
}) => {
  const { t, i18n } = useTranslation();
  const providerDetailsKey =
    organisation?.providerName?.toLowerCase() === 'group'
      ? 'organisation.group-details'
      : `organisation.${organisation?.providerName?.toLowerCase()}-details`;

  const useWideLayout =
    windowWidth <= ENTITY_DETAILS_BREAKPOINT ||
    windowHeight <= ENTITY_DETAILS_HEIGHT_BREAKPOINT;

  const cardWidth = inlineBesideMain
    ? `${TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH}px`
    : useWideLayout
      ? hasAttachments
        ? '1020px'
        : '680px'
      : '320px';

  const visibleFields: React.ReactNode[] = [];

  if (organisation?.legalName) {
    visibleFields.push(
      <CardInfo
        key="legalName"
        label={t('organisation.company-name')}
        data={organisation?.legalName}
      />
    );
  }
  if (organisation?.friendlyName) {
    visibleFields.push(
      <CardInfo
        key="friendlyName"
        label={t('organisation.friendly-name')}
        data={organisation?.friendlyName}
      />
    );
  }
  if (organisation?.organisationDetails?.alternateName) {
    visibleFields.push(
      <CardInfo
        key="alternateName"
        label={t('organisation.alternate-name')}
        data={organisation?.organisationDetails?.alternateName}
      />
    );
  }
  if (organisation?.organisationDetails?.code) {
    visibleFields.push(
      <CardInfo
        key="code"
        label={t('organisation.code')}
        data={organisation?.organisationDetails?.code}
      />
    );
  }
  if (organisation?.creator) {
    visibleFields.push(
      <CardInfo
        key="creator"
        label={t('organisation.created-by')}
        data={organisation?.creator}
      />
    );
  }
  if (organisation?.createTimestamp) {
    visibleFields.push(
      <CardInfo
        key="createTimestamp"
        label={t('organisation.created-on')}
        data={formatDisplayDate(
          organisation?.createTimestamp,
          'DD/MM/YYYY',
          i18n.language
        )}
      />
    );
  }
  if (organisation?.currency) {
    visibleFields.push(
      <CardInfo
        key="currency"
        label={t('common.currency')}
        data={organisation?.currency}
      />
    );
  }
  if (organisation?.taxNumber) {
    visibleFields.push(
      <CardInfo
        key="taxNumber"
        label={t('organisation.tax-number')}
        data={organisation?.taxNumber}
      />
    );
  }
  if (organisation?.organisationDetails?.contactPersonName) {
    visibleFields.push(
      <CardInfo
        key="contactPersonName"
        label={t('organisation.contact-person-name')}
        data={organisation?.organisationDetails?.contactPersonName}
      />
    );
  }
  if (organisation?.organisationDetails?.contactPersonEmail) {
    visibleFields.push(
      <CardInfo
        key="contactPersonEmail"
        label={t('organisation.contact-person-email')}
        data={organisation?.organisationDetails?.contactPersonEmail}
      />
    );
  }
  if (organisation?.organisationDetails?.contactPersonTelephone) {
    visibleFields.push(
      <CardInfo
        key="contactPersonTelephone"
        label={t('organisation.contact-person-telephone')}
        data={organisation?.organisationDetails?.contactPersonTelephone}
      />
    );
  }
  if (organisation?.organisationDetails?.registeredAddress) {
    visibleFields.push(
      <CardInfo
        key="registeredAddress"
        label={t('organisation.registered-address')}
        data={organisation?.organisationDetails?.registeredAddress}
      />
    );
  }
  if (organisation?.country) {
    visibleFields.push(
      <CardInfo
        key="country"
        label={t('common.country')}
        data={getCountry(organisation?.country)}
      />
    );
  }
  //if (organisation?.organisationDetails?.notes) {
  //  visibleFields.push(
  //    <CardInfo
  //    key="notes"
  //    label={t('organisation.notes')}
  //    data={organisation?.organisationDetails?.notes}
  //    />
  //  );
  //}

  const midPoint = Math.ceil(visibleFields.length / 2);
  const leftColumnFields = useWideLayout
    ? visibleFields.slice(0, midPoint)
    : visibleFields;
  const rightColumnFields = useWideLayout ? visibleFields.slice(midPoint) : [];

  const attachments = organisation?.organisationDetails?.attachments || [];
  const hasAnyAttachments = attachments.length > 0;

  return (
    <StyledCard
      width={cardWidth}
      style={{ marginTop: inlineBesideMain ? 0 : '20px' }}
    >
      <div style={{ overflow: 'visible' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <Space style={{ columnGap: 10,alignItems: 'center'  }}>
            <ProviderLogoNav
              providerName={organisation?.providerName}
              shortCode={organisation?.shortCode}
              organisationId={organisation?.uuid}
              style={{ height: '32px', display: 'block' }}
            >
              {renderProviderLogo(organisation?.providerName)}
            </ProviderLogoNav>
            {organisation && (
              <CardTitleSpan>{t(providerDetailsKey)}</CardTitleSpan>
            )}
          </Space>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 10,
            }}
          >
            {currentUser &&
              AccountType[currentUser.accountType] !== AccountType.CLIENT && (
                <div
                  className="edit-details-link"
                  role="button"
                  tabIndex={0}
                  aria-label={t('common.edit')}
                  onClick={onEditFriendlyName}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onEditFriendlyName();
                    }
                  }}
                >
                  <EditOutlined />
                </div>
              )}
            {(windowWidth <= ENTITY_DETAILS_BREAKPOINT ||
              windowHeight <= ENTITY_DETAILS_HEIGHT_BREAKPOINT) && (
              <CloseOutlined
                role="button"
                tabIndex={0}
                aria-label={t('common.close')}
                onClick={onClose}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClose();
                  }
                }}
              />
            )}
          </div>
        </div>
        <Divider style={{ margin: '16px 0' }} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '4px',
            flexDirection: useWideLayout ? 'row' : 'column',
            gap: useWideLayout ? 20 : 0,
            overflow: 'visible',
            maxWidth: inlineBesideMain ? '100%' : 1000,
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: 5,
              flex: 1,
            }}
          >
            {leftColumnFields}
          </div>
          {useWideLayout && rightColumnFields.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                rowGap: 5,
                flex: 1,
              }}
            >
              {rightColumnFields}
            </div>
          )}
          {useWideLayout && hasAnyAttachments && (
            <div
              className="scope-attachments-section"
              style={{
                display: 'flex',
                flexDirection: 'column',
                rowGap: 5,
                flex: 1,
              }}
            >
              <CardTitleSpan
                className="scope-attachments-section-title"
                style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}
              >
                {t('organisation.attachments-title')}
              </CardTitleSpan>
              {attachments.map((attachment) => (
                <div
                  key={attachment.uuid}
                  className="scope-attachments-row"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid var(--color-border, #dbeae3)',
                  }}
                >
                  <span>{attachment.description}</span>
                  <DownloadOutlined
                    style={{
                      fontSize: '16px',
                      cursor: 'pointer',
                      color: 'var(--color-primary, #41aaaf)',
                    }}
                    onClick={() => {
                      downloadAttachment(organisation!.uuid, attachment.uuid);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        {!useWideLayout && hasAnyAttachments && (
          <div className="scope-attachments-section">
            <div
              className="scope-attachments-heading"
              role="heading"
              aria-level={3}
            >
              {t('organisation.attachments-title')}
            </div>
            <div>
              {attachments.map((attachment) => (
                <div
                  key={attachment.uuid}
                  className="scope-attachments-row"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid var(--color-border, #dbeae3)',
                  }}
                >
                  <span>{attachment.description}</span>
                  <DownloadOutlined
                    style={{
                      fontSize: '16px',
                      cursor: 'pointer',
                      color: 'var(--color-primary, #41aaaf)',
                    }}
                    onClick={() => {
                      downloadAttachment(organisation!.uuid, attachment.uuid);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </StyledCard>
  );
};

